import type { LoggerContextConfig } from '../loggers/helpers/LoggerContext';

export enum LogLevel {
  DEBUG = 'DEBUG',
  ERROR = 'ERROR',
  INFO = 'INFO',
  WARN = 'WARN'
}

/**
 * Wire-format types for the telemetry agent and metrics service.
 * These are the on-the-wire shapes between the client agent and services/metrics.
 */

/** Context sent with every telemetry batch. */
export interface AgentContext {
  app: string;
  appEnv: string;
  appVersion: string;
  deviceId?: string;
  sessionId?: string;
  userId?: string;
}

/** A single log event sent by the telemetry agent. */
export interface LogEvent {
  level: LogLevel;
  message: string;
  ts: number;
  data?: Record<string, unknown>;
  /**
   * Optional machine-readable event key (eg: `app.error.thrown`) — present only for registered
   * events (§8.1); plain diagnostic logs omit it. Carries business events onto the log path.
   */
  event?: string;
}

/** Metric type discriminator. */
export enum MetricType {
  COUNTER = 'counter',
  GAUGE = 'gauge',
  HISTOGRAM = 'histogram'
}

/** A single metric event sent by the telemetry agent. */
export interface MetricEvent {
  name: string;
  type: MetricType;
  value: number;
  ts: number;
  labels?: Record<string, string>;
}

/** Request body for the log-ingest endpoint (a batch of log events plus shared context). */
export interface LogIngestRequest {
  context: AgentContext;
  events: LogEvent[];
}

/** Request body for the metric-ingest endpoint (a batch of metric events plus shared context). */
export interface MetricIngestRequest {
  context: AgentContext;
  events: MetricEvent[];
}

/** A single NDJSON line emitted by the log-ingest endpoint for CloudWatch subscription. */
export interface NdjsonLogLine {
  kind: 'log';
  context: AgentContext;
  event: LogEvent;
  receivedAt: number;
}

/** A single NDJSON line emitted by the metric-ingest endpoint for CloudWatch subscription. */
export interface NdjsonMetricLine {
  kind: 'metric';
  context: AgentContext;
  event: MetricEvent;
  receivedAt: number;
}

/**
 * A single entry in the metrics registry defining valid metric names, types, and label keys.
 * Used for type-safe metric recording.
 */
export interface MetricsRegistryEntry {
  /** The unique metric name (eg: 'http_request_duration_ms'). */
  name: string;
  /** The metric type (counter, gauge, or histogram). */
  type: MetricType;
  /** Optional description of what this metric measures. */
  description?: string;
  /** The allowed label keys for this metric (eg: ['method', 'status']). */
  labelKeys?: readonly string[];
}

/**
 * A typed metrics registry that defines all valid metrics for an application.
 * Apps create their own registry type extending this interface.
 */
export type MetricsRegistry = Record<string, MetricsRegistryEntry>;

/**
 * A single metric entry in the core telemetry registry.
 * `app`/`environment` are implicit context labels and must NOT appear in `labelKeys`.
 */
export interface MetricRegistryEntry {
  type: MetricType;
  labelKeys: readonly string[];
}

/** A single event entry in the core telemetry registry, keyed by its dotted event name. */
export interface EventRegistryEntry {
  fields: readonly string[];
}

/**
 * The core telemetry registry contract: the single source of truth for the metrics and
 * events shared across every app (distinct from the flat per-app `MetricsRegistry`).
 */
export interface ITelemetryRegistry {
  metrics: Record<string, MetricRegistryEntry>;
  events: Record<string, EventRegistryEntry>;
}

export type AdditionalDataPrimitives = string | number | boolean | object | null;

export type AdditionalData = {
  [key: string]: AdditionalDataPrimitives | AdditionalDataPrimitives[] | AdditionalData; // <-- We add AdditionalData again to allow nested objects with primitives.
} | null;

export type AdditionalDataWithError = AdditionalData | Error;

export type LogDataArray<T extends string> = [string, AdditionalData?, T[]?] | never;

export type LogDataArrayWithError<T extends string> = [string, AdditionalDataWithError?, T[]?] | never;

export interface IEventLogger<
  T extends string = never,
  DEBUG extends LogDataArray<T> = never,
  INFO extends LogDataArray<T> = never,
  WARN extends LogDataArray<T> = never,
  ERROR extends LogDataArrayWithError<T> = never
> {
  debug(arr: DEBUG): void;
  error(arr: ERROR): void;
  flush(): Promise<void>;
  info(arr: INFO): void;
  init(config: LoggerContextConfig): void;
  warn(arr: WARN): void;
}
